import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { SrsCardsTable } from "~/components/srs-cards-table";
import { useSrsCards } from "~/lib/client/srs-cards";
import { SuspenseFallback } from "~/components/ui/suspense-fallback";
import { Skeleton } from "~/components/ui/skeleton";
import { Input } from "~/components/ui/input";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "~/components/ui/table";

function SrsCardsTableSkeleton() {
	return (
		<div className="space-y-4">
			{/* Quick Guide Skeleton */}
			<div className="bg-blue-50 border border-blue-200 rounded-md p-4">
				<Skeleton className="h-4 w-48 mb-2" />
				<Skeleton className="h-3 w-full mb-1" />
				<Skeleton className="h-3 w-3/4" />
			</div>

			{/* Search and Filter Inputs */}
			<div className="flex gap-2">
				<Input placeholder="Search cards..." disabled />
				<Input placeholder="Filter by deck..." disabled />
			</div>

			{/* Table Skeleton */}
			<div className="bg-white border border-pink-200 rounded-md">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>
								<Skeleton className="h-4 w-16" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-20" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-24" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-20" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-16" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-20" />
							</TableHead>
							<TableHead>
								<Skeleton className="h-4 w-16" />
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{Array.from({ length: 10 }, (_, i) => i).map((id) => (
							<TableRow key={`skeleton-row-${id}`}>
								<TableCell>
									<Skeleton className="h-4 w-12" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-16" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-32" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-20" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-14" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-24" />
								</TableCell>
								<TableCell>
									<Skeleton className="h-4 w-16" />
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			</div>

			{/* Pagination Skeleton */}
			<div className="flex items-center justify-between">
				<Skeleton className="h-8 w-20" />
				<Skeleton className="h-4 w-24" />
				<Skeleton className="h-8 w-16" />
			</div>
		</div>
	);
}

export const Route = createFileRoute("/app/srs-cards/")({
	component: RouteComponent,
});

function RouteComponent() {
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [searchTerm, setSearchTerm] = useState("");
	const [deckFilter, setDeckFilter] = useState("");

	return (
		<div className="max-w-screen-md mx-auto px-3 sm:px-4 pt-4 pb-16">
			<h1 className="text-2xl font-semibold text-pink-600 mb-4">
				Spaced Repetition Cards
			</h1>

			<SuspenseFallback
				loading={<SrsCardsTableSkeleton />}
				error={
					<div className="text-center p-8 text-red-600">
						Error loading SRS cards. Please try refreshing the page.
					</div>
				}
			>
				<SrsCardsContent
					page={page}
					pageSize={pageSize}
					searchTerm={searchTerm}
					deckFilter={deckFilter}
					onPageChange={setPage}
					onPageSizeChange={setPageSize}
					onSearchTermChange={setSearchTerm}
					onDeckFilterChange={setDeckFilter}
				/>
			</SuspenseFallback>
		</div>
	);
}

function SrsCardsContent({
	page,
	pageSize,
	searchTerm,
	deckFilter,
	onPageChange,
	onPageSizeChange,
	onSearchTermChange,
	onDeckFilterChange,
}: {
	page: number;
	pageSize: number;
	searchTerm: string;
	deckFilter: string;
	onPageChange: (page: number) => void;
	onPageSizeChange: (pageSize: number) => void;
	onSearchTermChange: (searchTerm: string) => void;
	onDeckFilterChange: (deckFilter: string) => void;
}) {
	const { data } = useSrsCards({
		page,
		pageSize,
		searchTerm,
		deckFilter,
	});

	return (
		<SrsCardsTable
			cards={data?.cards ?? []}
			total={data?.total ?? 0}
			page={page}
			pageSize={pageSize}
			totalPages={data?.totalPages ?? 0}
			isLoading={false}
			searchTerm={searchTerm}
			deckFilter={deckFilter}
			onPageChange={onPageChange}
			onPageSizeChange={onPageSizeChange}
			onSearchTermChange={onSearchTermChange}
			onDeckFilterChange={onDeckFilterChange}
		/>
	);
}
